import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Users, Plus, Upload, Trash2, Search, Mail, Phone,
  Download, RefreshCw, X, UserPlus, FileSpreadsheet, ArrowLeft,
  StickyNote, Edit2, Save, Clock, Send, ChevronRight, Calendar,
  Bell, CheckCircle, AlertCircle, User, Building, Home, Briefcase
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Contact type options
const CONTACT_TYPES = [
  { value: 'buyer', label: 'Buyer', icon: User },
  { value: 'seller', label: 'Seller', icon: Home },
  { value: 'investor', label: 'Investor', icon: Briefcase },
  { value: 'tenant', label: 'Tenant', icon: User },
  { value: 'landlord', label: 'Landlord', icon: Building },
  { value: 'lender', label: 'Lender', icon: Briefcase },
];

// Relationship stage options
const RELATIONSHIP_STAGES = [
  { value: 'new_lead', label: 'New Lead', color: 'bg-blue-500' },
  { value: 'active_client', label: 'Active Client', color: 'bg-green-500' },
  { value: 'under_contract', label: 'Under Contract', color: 'bg-yellow-500' },
  { value: 'closed', label: 'Closed', color: 'bg-purple-500' },
  { value: 'past_client', label: 'Past Client', color: 'bg-gray-500' },
  { value: 'referral_partner', label: 'Referral Partner', color: 'bg-pink-500' },
];

const Contacts = () => {
  const navigate = useNavigate();
  const { token } = useAuth();
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStage, setFilterStage] = useState('all');
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [newContact, setNewContact] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    notes: '',
    contact_type: '',
    relationship_stage: 'new_lead',
    follow_up_date: ''
  });
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);
  
  // Contact detail state
  const [selectedContact, setSelectedContact] = useState(null);
  const [contactDetail, setContactDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({ 
    name: '', 
    email: '', 
    phone: '', 
    notes: '',
    contact_type: '',
    relationship_stage: '',
    follow_up_date: ''
  });
  const [saving, setSaving] = useState(false);
  const [newNote, setNewNote] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  
  const authHeaders = { Authorization: `Bearer ${token}` };
  
  useEffect(() => {
    fetchContacts();
  }, []);
  
  const fetchContacts = async () => {
    try {
      const response = await axios.get(`${API}/contacts`, { headers: authHeaders });
      setContacts(response.data);
    } catch (error) {
      console.error('Error fetching contacts:', error);
      toast.error('Failed to load contacts');
    } finally {
      setLoading(false);
    }
  };
  
  const fetchContactDetail = async (contactId) => {
    setLoadingDetail(true);
    try {
      const response = await axios.get(`${API}/contacts/${contactId}`, { headers: authHeaders });
      setContactDetail(response.data);
      setEditData({
        name: response.data.name || '',
        email: response.data.email || '',
        phone: response.data.phone || '',
        notes: response.data.notes || '',
        contact_type: response.data.contact_type || '',
        relationship_stage: response.data.relationship_stage || '',
        follow_up_date: response.data.follow_up_date || ''
      });
    } catch (error) {
      console.error('Error fetching contact:', error);
      toast.error('Failed to load contact details');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Inline update for type/stage from table
  const handleInlineUpdate = async (contactId, field, value) => {
    try {
      await axios.put(
        `${API}/contacts/${contactId}`,
        { [field]: value },
        { headers: authHeaders }
      );
      // Update local state immediately
      setContacts(prev => prev.map(c => 
        c.id === contactId ? { ...c, [field]: value } : c
      ));
      // Also update detail if open
      if (contactDetail?.id === contactId) {
        setContactDetail(prev => ({ ...prev, [field]: value }));
      }
      toast.success('Updated successfully');
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update');
    }
  };
  
  const handleSelectContact = (contact) => {
    setSelectedContact(contact);
    setEditMode(false);
    fetchContactDetail(contact.id);
  };
  
  const handleCloseDetail = () => {
    setSelectedContact(null);
    setContactDetail(null);
    setEditMode(false);
  };
  
  const handleAddContact = async () => {
    if (!newContact.email) {
      toast.error('Email is required');
      return;
    }
    
    try {
      await axios.post(`${API}/contacts`, newContact, { headers: authHeaders });
      toast.success('Contact added!');
      setShowAddDialog(false);
      setNewContact({ 
        name: '', 
        email: '', 
        phone: '', 
        notes: '',
        contact_type: '',
        relationship_stage: 'new_lead',
        follow_up_date: ''
      });
      fetchContacts();
    } catch (error) {
      console.error('Error adding contact:', error);
      toast.error('Failed to add contact');
    }
  };
  
  const handleUpdateContact = async () => {
    if (!selectedContact) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/contacts/${selectedContact.id}`, editData, { headers: authHeaders });
      toast.success('Contact updated!');
      setEditMode(false);
      fetchContacts();
      fetchContactDetail(selectedContact.id);
    } catch (error) {
      console.error('Error updating contact:', error);
      toast.error('Failed to update contact');
    } finally {
      setSaving(false);
    }
  };
  
  const handleDeleteContact = async (contactId) => {
    if (!window.confirm('Delete this contact?')) return;
    
    try {
      await axios.delete(`${API}/contacts/${contactId}`, { headers: authHeaders });
      toast.success('Contact deleted');
      setContacts(contacts.filter(c => c.id !== contactId));
      if (selectedContact?.id === contactId) {
        handleCloseDetail();
      }
    } catch (error) {
      console.error('Error deleting contact:', error);
      toast.error('Failed to delete contact');
    }
  };
  
  const handleAddNote = async () => {
    if (!newNote.trim() || !selectedContact) return;
    
    setAddingNote(true);
    try {
      await axios.post(
        `${API}/contacts/${selectedContact.id}/notes`,
        { note: newNote },
        { headers: authHeaders }
      );
      toast.success('Note added!');
      setNewNote('');
      fetchContactDetail(selectedContact.id);
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };
  
  const handleDeleteNote = async (noteId) => {
    if (!selectedContact) return;
    
    try {
      await axios.delete(`${API}/contacts/${selectedContact.id}/notes/${noteId}`, { headers: authHeaders });
      toast.success('Note deleted');
      fetchContactDetail(selectedContact.id);
    } catch (error) {
      console.error('Error deleting note:', error);
      toast.error('Failed to delete note');
    }
  };

  const handleMarkContacted = async () => {
    if (!selectedContact) return;
    
    try {
      await axios.post(`${API}/contacts/${selectedContact.id}/mark-contacted`, {}, { headers: authHeaders });
      toast.success('Marked as contacted!');
      fetchContacts();
      fetchContactDetail(selectedContact.id);
    } catch (error) {
      console.error('Error marking contacted:', error);
      toast.error('Failed to update');
    }
  };

  // Helper to check if follow-up is due today or overdue
  const getFollowUpStatus = (followUpDate) => {
    if (!followUpDate) return null;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const followUp = new Date(followUpDate);
    followUp.setHours(0, 0, 0, 0);
    
    if (followUp.getTime() === today.getTime()) return 'today';
    if (followUp < today) return 'overdue';
    return 'upcoming';
  };

  // Get contacts with follow-ups due today
  const followUpsDueToday = contacts.filter(c => {
    const status = getFollowUpStatus(c.follow_up_date);
    return status === 'today' || status === 'overdue';
  });
  
  const handleImportCSV = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImporting(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await axios.post(
        `${API}/contacts/import-csv`,
        formData,
        { 
          headers: { 
            ...authHeaders,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      toast.success(`Imported ${response.data.imported} contacts! (${response.data.skipped} skipped)`);
      setShowImportDialog(false);
      fetchContacts();
    } catch (error) {
      console.error('Error importing CSV:', error);
      toast.error('Failed to import CSV');
    } finally {
      setImporting(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  const exportCSV = () => {
    const headers = ['name', 'email', 'phone', 'type', 'stage', 'follow_up_date', 'last_contacted'];
    const csvContent = [
      headers.join(','),
      ...contacts.map(c => `"${c.name || ''}","${c.email}","${c.phone || ''}","${c.contact_type || ''}","${c.relationship_stage || ''}","${c.follow_up_date || ''}","${c.last_contacted || ''}"`)
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'contacts.csv';
    a.click();
    URL.revokeObjectURL(url);
    toast.success('Contacts exported!');
  };

  // Get label helpers
  const getContactTypeLabel = (type) => {
    const found = CONTACT_TYPES.find(t => t.value === type);
    return found ? found.label : type;
  };

  const getStageLabel = (stage) => {
    const found = RELATIONSHIP_STAGES.find(s => s.value === stage);
    return found ? found.label : stage;
  };

  const getStageColor = (stage) => {
    const found = RELATIONSHIP_STAGES.find(s => s.value === stage);
    return found ? found.color : 'bg-gray-500';
  };
  
  const filteredContacts = contacts.filter(c => {
    const matchesSearch = c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = filterType === 'all' || c.contact_type === filterType;
    const matchesStage = filterStage === 'all' || c.relationship_stage === filterStage;
    return matchesSearch && matchesType && matchesStage;
  });

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatShortDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Follow-up Reminder Banner */}
      {followUpsDueToday.length > 0 && (
        <div className="bg-yellow-500/10 border-b border-yellow-500/20">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center gap-3">
              <Bell className="text-yellow-600" size={20} />
              <span className="font-medium text-yellow-700">
                {followUpsDueToday.length} follow-up{followUpsDueToday.length > 1 ? 's' : ''} due today!
              </span>
              <div className="flex gap-2 ml-auto">
                {followUpsDueToday.slice(0, 3).map(c => (
                  <Button 
                    key={c.id} 
                    variant="outline" 
                    size="sm"
                    className="text-yellow-700 border-yellow-500/50"
                    onClick={() => handleSelectContact(c)}
                  >
                    {c.name || c.email.split('@')[0]}
                  </Button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Header */}
      <div className="border-b bg-card">
        <div className="container mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon"
                onClick={() => navigate('/dashboard')}
                className="shrink-0"
              >
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-2xl font-bold flex items-center gap-2">
                  <Users className="text-primary" />
                  Contacts
                </h1>
                <p className="text-muted-foreground mt-1">
                  Manage your client contact list ({contacts.length} contacts)
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-3">
              {/* Import Dialog */}
              <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="gap-2">
                    <Upload size={16} />
                    Import CSV
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Import Contacts from CSV</DialogTitle>
                    <DialogDescription>
                      Upload a CSV file with columns: name, email, phone
                    </DialogDescription>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div className="border-2 border-dashed rounded-lg p-8 text-center">
                      <FileSpreadsheet className="mx-auto text-muted-foreground mb-3" size={40} />
                      <p className="text-sm text-muted-foreground mb-3">
                        CSV file should have headers: name, email, phone
                      </p>
                      <input
                        ref={fileInputRef}
                        type="file"
                        accept=".csv"
                        onChange={handleImportCSV}
                        className="hidden"
                        id="csv-upload"
                      />
                      <Button 
                        variant="outline" 
                        onClick={() => fileInputRef.current?.click()}
                        disabled={importing}
                      >
                        {importing ? (
                          <><RefreshCw size={16} className="animate-spin mr-2" /> Importing...</>
                        ) : (
                          <>Select CSV File</>
                        )}
                      </Button>
                    </div>
                    
                    <div className="text-xs text-muted-foreground">
                      <p className="font-medium mb-1">Example CSV format:</p>
                      <pre className="bg-secondary/50 p-2 rounded text-xs">
{`name,email,phone
John Smith,john@email.com,555-1234
Jane Doe,jane@email.com,555-5678`}
                      </pre>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
              
              <Button variant="outline" onClick={exportCSV} className="gap-2">
                <Download size={16} />
                Export
              </Button>
              
              {/* Add Contact Dialog */}
              <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
                <DialogTrigger asChild>
                  <Button className="gap-2">
                    <UserPlus size={16} />
                    Add Contact
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Add New Contact</DialogTitle>
                  </DialogHeader>
                  
                  <div className="space-y-4 py-4">
                    <div>
                      <Label htmlFor="name">Name</Label>
                      <Input
                        id="name"
                        value={newContact.name}
                        onChange={(e) => setNewContact({...newContact, name: e.target.value})}
                        placeholder="John Smith"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        type="email"
                        value={newContact.email}
                        onChange={(e) => setNewContact({...newContact, email: e.target.value})}
                        placeholder="john@email.com"
                      />
                    </div>
                    <div>
                      <Label htmlFor="phone">Phone</Label>
                      <Input
                        id="phone"
                        value={newContact.phone}
                        onChange={(e) => setNewContact({...newContact, phone: e.target.value})}
                        placeholder="555-1234"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Contact Type</Label>
                        <Select 
                          value={newContact.contact_type} 
                          onValueChange={(v) => setNewContact({...newContact, contact_type: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {CONTACT_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Stage</Label>
                        <Select 
                          value={newContact.relationship_stage} 
                          onValueChange={(v) => setNewContact({...newContact, relationship_stage: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select stage" />
                          </SelectTrigger>
                          <SelectContent>
                            {RELATIONSHIP_STAGES.map(stage => (
                              <SelectItem key={stage.value} value={stage.value}>
                                <div className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full ${stage.color}`} />
                                  {stage.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="follow_up">Follow-up Date</Label>
                      <Input
                        id="follow_up"
                        type="date"
                        value={newContact.follow_up_date}
                        onChange={(e) => setNewContact({...newContact, follow_up_date: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label htmlFor="notes">Notes</Label>
                      <Textarea
                        id="notes"
                        value={newContact.notes}
                        onChange={(e) => setNewContact({...newContact, notes: e.target.value})}
                        placeholder="Add any notes about this contact..."
                        rows={3}
                      />
                    </div>
                  </div>
                  
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowAddDialog(false)}>
                      Cancel
                    </Button>
                    <Button onClick={handleAddContact}>
                      Add Contact
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </div>
      </div>
      
      {/* Main Content */}
      <div className="container mx-auto px-4 py-6">
        {/* Search and Filters */}
        <div className="mb-6 flex flex-wrap gap-4">
          <div className="relative flex-1 min-w-[200px] max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={18} />
            <Input
              placeholder="Search contacts..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <Select value={filterType} onValueChange={setFilterType}>
            <SelectTrigger className="w-[150px]">
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {CONTACT_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={filterStage} onValueChange={setFilterStage}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Stages" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Stages</SelectItem>
              {RELATIONSHIP_STAGES.map(stage => (
                <SelectItem key={stage.value} value={stage.value}>
                  <div className="flex items-center gap-2">
                    <span className={`w-2 h-2 rounded-full ${stage.color}`} />
                    {stage.label}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Contacts Table */}
        <Card>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <RefreshCw size={32} className="animate-spin text-primary" />
              </div>
            ) : filteredContacts.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Contact</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Stage</TableHead>
                    <TableHead>Follow-up</TableHead>
                    <TableHead className="w-[100px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredContacts.map((contact) => {
                    const followUpStatus = getFollowUpStatus(contact.follow_up_date);
                    return (
                    <TableRow 
                      key={contact.id}
                      className={`cursor-pointer hover:bg-muted/50 ${followUpStatus === 'overdue' ? 'bg-red-50 dark:bg-red-950/20' : followUpStatus === 'today' ? 'bg-yellow-50 dark:bg-yellow-950/20' : ''}`}
                      onClick={() => handleSelectContact(contact)}
                    >
                      <TableCell className="font-medium">{contact.name || '-'}</TableCell>
                      <TableCell>
                        <div className="space-y-1">
                          <span className="flex items-center gap-1 text-primary text-sm">
                            <Mail size={12} />
                            {contact.email}
                          </span>
                          {contact.phone && (
                            <span className="flex items-center gap-1 text-muted-foreground text-xs">
                              <Phone size={12} />
                              {contact.phone}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Select 
                          value={contact.contact_type || ''} 
                          onValueChange={(v) => handleInlineUpdate(contact.id, 'contact_type', v)}
                        >
                          <SelectTrigger className="h-8 w-[120px] text-xs">
                            <SelectValue placeholder="Select type">
                              {contact.contact_type ? getContactTypeLabel(contact.contact_type) : 'Select type'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {CONTACT_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <Select 
                          value={contact.relationship_stage || ''} 
                          onValueChange={(v) => handleInlineUpdate(contact.id, 'relationship_stage', v)}
                        >
                          <SelectTrigger className={`h-8 w-[140px] text-xs ${contact.relationship_stage ? getStageColor(contact.relationship_stage) + ' text-white border-0' : ''}`}>
                            <SelectValue placeholder="Select stage">
                              {contact.relationship_stage ? getStageLabel(contact.relationship_stage) : 'Select stage'}
                            </SelectValue>
                          </SelectTrigger>
                          <SelectContent>
                            {RELATIONSHIP_STAGES.map(stage => (
                              <SelectItem key={stage.value} value={stage.value}>
                                <div className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full ${stage.color}`} />
                                  {stage.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-1">
                          {followUpStatus === 'overdue' && <AlertCircle size={14} className="text-red-500 shrink-0" />}
                          {followUpStatus === 'today' && <Bell size={14} className="text-yellow-500 shrink-0" />}
                          <Input
                            type="date"
                            value={contact.follow_up_date || ''}
                            onChange={(e) => handleInlineUpdate(contact.id, 'follow_up_date', e.target.value)}
                            className={`h-8 w-[150px] text-xs cursor-pointer ${followUpStatus === 'overdue' ? 'border-red-500 text-red-600' : followUpStatus === 'today' ? 'border-yellow-500 text-yellow-600' : ''}`}
                          />
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleSelectContact(contact);
                            }}
                            className="text-muted-foreground hover:text-primary"
                          >
                            <ChevronRight size={16} />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteContact(contact.id);
                            }}
                            className="text-muted-foreground hover:text-destructive"
                          >
                            <Trash2 size={16} />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  )})}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-20">
                <Users size={48} className="mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground mb-4">
                  {searchTerm ? 'No contacts match your search' : 'No contacts yet'}
                </p>
                {!searchTerm && (
                  <div className="flex justify-center gap-3">
                    <Button onClick={() => setShowAddDialog(true)} className="gap-2">
                      <UserPlus size={16} />
                      Add Contact
                    </Button>
                    <Button variant="outline" onClick={() => setShowImportDialog(true)} className="gap-2">
                      <Upload size={16} />
                      Import CSV
                    </Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
      
      {/* Contact Detail Sheet */}
      <Sheet open={!!selectedContact} onOpenChange={(open) => !open && handleCloseDetail()}>
        <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
          <SheetHeader>
            <SheetTitle className="flex items-center justify-between">
              <span>Contact Details</span>
              {!editMode && (
                <Button variant="outline" size="sm" onClick={() => setEditMode(true)} className="gap-2">
                  <Edit2 size={14} />
                  Edit
                </Button>
              )}
            </SheetTitle>
          </SheetHeader>
          
          {loadingDetail ? (
            <div className="flex items-center justify-center py-20">
              <RefreshCw size={24} className="animate-spin text-primary" />
            </div>
          ) : contactDetail ? (
            <div className="mt-6 space-y-6">
              {/* Contact Info */}
              <div className="space-y-4">
                {editMode ? (
                  <>
                    <div>
                      <Label>Name</Label>
                      <Input
                        value={editData.name}
                        onChange={(e) => setEditData({...editData, name: e.target.value})}
                        placeholder="Contact name"
                      />
                    </div>
                    <div>
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={editData.email}
                        onChange={(e) => setEditData({...editData, email: e.target.value})}
                        placeholder="Email address"
                      />
                    </div>
                    <div>
                      <Label>Phone</Label>
                      <Input
                        value={editData.phone}
                        onChange={(e) => setEditData({...editData, phone: e.target.value})}
                        placeholder="Phone number"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Contact Type</Label>
                        <Select 
                          value={editData.contact_type || ''} 
                          onValueChange={(v) => setEditData({...editData, contact_type: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                          <SelectContent>
                            {CONTACT_TYPES.map(type => (
                              <SelectItem key={type.value} value={type.value}>
                                {type.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Stage</Label>
                        <Select 
                          value={editData.relationship_stage || ''} 
                          onValueChange={(v) => setEditData({...editData, relationship_stage: v})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Select stage" />
                          </SelectTrigger>
                          <SelectContent>
                            {RELATIONSHIP_STAGES.map(stage => (
                              <SelectItem key={stage.value} value={stage.value}>
                                <div className="flex items-center gap-2">
                                  <span className={`w-2 h-2 rounded-full ${stage.color}`} />
                                  {stage.label}
                                </div>
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div>
                      <Label>Follow-up Date</Label>
                      <Input
                        type="date"
                        value={editData.follow_up_date || ''}
                        onChange={(e) => setEditData({...editData, follow_up_date: e.target.value})}
                      />
                    </div>
                    <div>
                      <Label>General Notes</Label>
                      <Textarea
                        value={editData.notes}
                        onChange={(e) => setEditData({...editData, notes: e.target.value})}
                        placeholder="General notes about this contact"
                        rows={3}
                      />
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={handleUpdateContact} disabled={saving} className="gap-2">
                        {saving ? (
                          <RefreshCw size={14} className="animate-spin" />
                        ) : (
                          <Save size={14} />
                        )}
                        Save Changes
                      </Button>
                      <Button variant="outline" onClick={() => setEditMode(false)}>
                        Cancel
                      </Button>
                    </div>
                  </>
                ) : (
                  <Card>
                    <CardContent className="p-4 space-y-3">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                          <Users className="text-primary" size={24} />
                        </div>
                        <div className="flex-1">
                          <h3 className="font-semibold text-lg">{contactDetail.name || 'No name'}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            {contactDetail.contact_type && (
                              <Badge variant="outline" className="text-xs">
                                {getContactTypeLabel(contactDetail.contact_type)}
                              </Badge>
                            )}
                            {contactDetail.relationship_stage && (
                              <Badge className={`${getStageColor(contactDetail.relationship_stage)} text-white text-xs`}>
                                {getStageLabel(contactDetail.relationship_stage)}
                              </Badge>
                            )}
                          </div>
                        </div>
                      </div>
                      
                      <div className="pt-2 space-y-2">
                        <a 
                          href={`mailto:${contactDetail.email}`}
                          className="flex items-center gap-2 text-primary hover:underline"
                        >
                          <Mail size={16} />
                          {contactDetail.email}
                        </a>
                        {contactDetail.phone && (
                          <a 
                            href={`tel:${contactDetail.phone}`}
                            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
                          >
                            <Phone size={16} />
                            {contactDetail.phone}
                          </a>
                        )}
                      </div>
                      
                      {/* Follow-up & Last Contacted */}
                      <div className="pt-3 border-t space-y-2">
                        <div className="flex items-center justify-between text-sm">
                          <span className="text-muted-foreground flex items-center gap-2">
                            <Clock size={14} />
                            Last Contacted:
                          </span>
                          <span className={contactDetail.last_contacted ? '' : 'text-muted-foreground'}>
                            {contactDetail.last_contacted ? formatShortDate(contactDetail.last_contacted) : 'Never'}
                          </span>
                        </div>
                        {contactDetail.follow_up_date && (
                          <div className="flex items-center justify-between text-sm">
                            <span className="text-muted-foreground flex items-center gap-2">
                              <Calendar size={14} />
                              Follow-up:
                            </span>
                            <span className={`flex items-center gap-1 ${getFollowUpStatus(contactDetail.follow_up_date) === 'overdue' ? 'text-red-600 font-medium' : getFollowUpStatus(contactDetail.follow_up_date) === 'today' ? 'text-yellow-600 font-medium' : ''}`}>
                              {getFollowUpStatus(contactDetail.follow_up_date) === 'overdue' && <AlertCircle size={14} />}
                              {getFollowUpStatus(contactDetail.follow_up_date) === 'today' && <Bell size={14} />}
                              {formatShortDate(contactDetail.follow_up_date)}
                            </span>
                          </div>
                        )}
                        <Button 
                          variant="outline" 
                          size="sm" 
                          className="w-full mt-2 gap-2"
                          onClick={handleMarkContacted}
                        >
                          <CheckCircle size={14} />
                          Mark as Contacted
                        </Button>
                      </div>
                      
                      {contactDetail.notes && (
                        <div className="pt-2 border-t">
                          <p className="text-sm text-muted-foreground">{contactDetail.notes}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
              
              {/* Notes Section */}
              {!editMode && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold flex items-center gap-2">
                      <StickyNote size={18} />
                      Notes & Updates
                    </h4>
                    <Badge variant="secondary">{contactDetail.notes_history?.length || 0}</Badge>
                  </div>
                  
                  {/* Add Note */}
                  <div className="space-y-2">
                    <Textarea
                      value={newNote}
                      onChange={(e) => setNewNote(e.target.value)}
                      placeholder="Add a note or update..."
                      rows={2}
                    />
                    <Button 
                      onClick={handleAddNote} 
                      disabled={!newNote.trim() || addingNote}
                      size="sm"
                      className="gap-2"
                    >
                      {addingNote ? (
                        <RefreshCw size={14} className="animate-spin" />
                      ) : (
                        <Send size={14} />
                      )}
                      Add Note
                    </Button>
                  </div>
                  
                  {/* Notes List */}
                  <div className="space-y-3 max-h-[300px] overflow-y-auto">
                    {contactDetail.notes_history?.length > 0 ? (
                      contactDetail.notes_history.map((note) => (
                        <div 
                          key={note.id}
                          className="p-3 bg-secondary/30 rounded-lg group relative"
                        >
                          <p className="text-sm pr-8">{note.note}</p>
                          <div className="flex items-center gap-1 mt-2 text-xs text-muted-foreground">
                            <Clock size={12} />
                            {formatDate(note.created_at)}
                          </div>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDeleteNote(note.id)}
                            className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 text-muted-foreground hover:text-destructive"
                          >
                            <X size={14} />
                          </Button>
                        </div>
                      ))
                    ) : (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No notes yet. Add your first note above.
                      </p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Delete Contact Button */}
              {!editMode && (
                <div className="pt-4 border-t">
                  <Button 
                    variant="outline" 
                    className="w-full text-destructive hover:bg-destructive hover:text-white gap-2"
                    onClick={() => handleDeleteContact(selectedContact.id)}
                  >
                    <Trash2 size={16} />
                    Delete Contact
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default Contacts;
